import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent } from '@theme/dialog';
import { isMobileBreakpoint } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { resolveLabelAnchor, resolveMappingLabelAnchor, calcLabelFontSize, loadBadgeImage } from '@theme/badge-canvas';

class UpsellLineProperties extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.#render();
  }

  get customMessageValue() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-upsell-field="custom-message"]'));
    return input?.value?.trim() || '';
  }

  get secondaryTextValue() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-upsell-field="secondary-text"]'));
    return input?.value?.trim() || '';
  }

  clearErrors() {
    const errors = this.querySelectorAll('.upsell-line-props__error');
    for (const error of errors) {
      if (!(error instanceof HTMLElement)) continue;
      error.textContent = '';
      error.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * @param {number} index
   * @param {string} message
   */
  showError(index, message) {
    const errors = this.querySelectorAll('.upsell-line-props__error');
    const target = errors[index];
    if (!(target instanceof HTMLElement)) return;

    target.textContent = message;
    target.setAttribute('aria-hidden', 'false');
  }

  /** @param {string} formId */
  setFormId(formId) {
    if (!formId) return;

    const inputs = this.querySelectorAll('input[data-upsell-field]');
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.setAttribute('form', formId);
    }
  }

  #render() {
    const customMessageLabel = this.dataset.customMessageLabel || 'Custom Message';
    const secondaryTextLabel = this.dataset.secondaryTextLabel || 'Secondary Text (optional)';
    const isCustomMessageRequired = this.dataset.customMessageRequired === 'true';

    this.classList.add('upsell-line-props', 'quick-add-visible');
    this.innerHTML = `
      <div class="upsell-line-props__field">
        <label class="upsell-line-props__label" for="upsell-custom-message">${customMessageLabel}</label>
        <input
          id="upsell-custom-message"
          class="upsell-line-props__input"
          type="text"
          minlength="2"
          maxlength="16"
          name="properties[Custom Message]"
          data-upsell-field="custom-message"
          autocomplete="off"
          ${isCustomMessageRequired ? 'required aria-required="true"' : ''}
        >
        <span class="upsell-line-props__error" data-upsell-error="custom-message" aria-hidden="true"></span>
      </div>
      <div class="upsell-line-props__field">
        <label class="upsell-line-props__label" for="upsell-secondary-text">${secondaryTextLabel}</label>
        <input
          id="upsell-secondary-text"
          class="upsell-line-props__input"
          type="text"
          maxlength="16"
          name="properties[Secondary Text]"
          data-upsell-field="secondary-text"
          autocomplete="off"
        >
        <span class="upsell-line-props__error" data-upsell-error="secondary-text" aria-hidden="true"></span>
      </div>
      <p class="upsell-line-props__error upsell-line-props__form-error" data-upsell-form-error aria-hidden="true"></p>
    `;

    this.#attachInputListeners();
  }

  #attachInputListeners() {
    const customMessageInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-upsell-field="custom-message"]'));
    if (!customMessageInput) return;
    const isCustomMessageRequired = this.dataset.customMessageRequired === 'true';

    const updateState = () => {
      const submitButton = this.#findSubmitButton();
      if (!(submitButton instanceof HTMLButtonElement)) return;
      if (!isCustomMessageRequired) {
        submitButton.disabled = false;
        return;
      }

      submitButton.disabled = !customMessageInput.value.trim();
    };

    customMessageInput.addEventListener('input', () => {
      updateState();
      this.showError(0, '');
    });

    updateState();
  }

  #findSubmitButton() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return null;

    return /** @type {HTMLButtonElement | null} */ (
      modalContent.querySelector('form button[type="submit"]') || modalContent.querySelector('button[type="submit"]')
    );
  }
}

/**
 * @typedef {HTMLElement & {
 *   setLoadingState?: (loading: boolean) => void,
 *   showDialog?: () => void,
 *   closeDialog?: () => void
 * }} QuickAddDialogElement
 */

export class UpsellQuickAddTrigger extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #submitAbortController = null;
  /** @type {AbortController | null} */
  #variantUpdateAbortController = null;
  /** @type {boolean} */
  #isSubmittingUpsell = false;
  /** @type {number | null} */
  #variantUpdateTimeoutId = null;
  /** @type {string | null} */
  #mainVariantId = null;
  /** @type {number} */
  #mainQty = 1;
  /** @type {string | null} */
  #mainSizeValue = null;
  /** @type {string | null} */
  #mainProductId = null;
  /** @type {string | null} */
  #mainProductHandle = null;
  /** @type {number} */
  #mainPrice = 0;
  /** @type {number} */
  #upsellPrice = 0;
  /** @type {string | null} */
  #selectedUpsellVariantId = null;
  /** @type {Array<{ id: number | string, price: number }> | null} */
  #cachedMainVariants = null;
  /** @type {Array<{ id: number | string, price: number }> | null} */
  #cachedUpsellVariants = null;
  /** @type {{ title: string, url: string, imageSrc: string, imageAlt: string, selectedOptions: Array<{ name: string, value: string }> }} */
  #mainProductContext = {
    title: '',
    url: '',
    imageSrc: '',
    imageAlt: '',
    selectedOptions: [],
  };
  /** @type {boolean | null} */
  #debugEnabled = null;
  /** @type {Array<{ message: string, context?: unknown, timestamp: string }>} */
  #debugLog = [];
  /** @type {Map<string, Promise<HTMLImageElement>>} */
  #imageCache = new Map();
  /** @type {(() => void) | null} */
  #badgeCanvasRenderFn = null;
  /** @type {number} */
  #badgeCanvasRenderGeneration = 0;
  /** @type {Element | null} */
  #cachedProductGrid = null;
  /** @type {Promise<Document | null> | null} */
  #pendingFetch = null;
  /** @type {boolean} */
  #mainColorUpdateInFlight = false;

  connectedCallback() {
    super.connectedCallback();
    const button = this.querySelector('button[type="button"]');
    if (button) {
      button.addEventListener('pointerenter', this.#prefetch, { once: true, passive: true });
      button.addEventListener('focusin', this.#prefetch, { once: true, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#submitAbortController?.abort();
    if (this.#variantUpdateTimeoutId !== null) {
      clearTimeout(this.#variantUpdateTimeoutId);
      this.#variantUpdateTimeoutId = null;
    }
  }

  /** Prefetches the upsell product page on hover/focus so the modal opens instantly. */
  #prefetch = () => {
    if (this.#cachedProductGrid || this.#pendingFetch) return;
    const productUrl = this.dataset.upsellProductUrl || '';
    if (!productUrl) return;
    this.#pendingFetch = this.fetchProductPage(productUrl)
      .then((html) => {
        const gridEl = html?.querySelector('[data-product-grid-content]');
        if (gridEl) {
          this.#cachedProductGrid = /** @type {Element} */ (gridEl.cloneNode(true));
        }
        this.#pendingFetch = null;
        return html;
      })
      .catch(() => {
        this.#pendingFetch = null;
        return null;
      });
  };

  /** @param {Event} event */
  handleClick = async (event) => {
    event.preventDefault();

    this.#captureMainProductState();
    this.#debug('Trigger click', {
      mainVariantId: this.#mainVariantId,
      mainQty: this.#mainQty,
      mainSizeValue: this.#mainSizeValue,
      mainPrice: this.#mainPrice,
      mainContext: this.#mainProductContext,
    });

    const triggerButton = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null;
    const dialogComponent = document.getElementById('quick-add-dialog');
    const quickAddDialog = dialogComponent instanceof HTMLElement ? /** @type {QuickAddDialogElement} */ (dialogComponent) : null;

    this.#setButtonLoadingState(triggerButton, true);

    if (quickAddDialog) {
      this.#stayVisibleUntilDialogCloses(quickAddDialog);
      quickAddDialog.setLoadingState?.(true);
      quickAddDialog.showDialog?.();
    }

    try {
      const productUrl = this.dataset.upsellProductUrl || '';

      let productGrid = this.#cachedProductGrid
        ? /** @type {Element} */ (this.#cachedProductGrid.cloneNode(true))
        : null;

      if (!productGrid) {
        const htmlPromise = this.#pendingFetch ?? this.fetchProductPage(productUrl);
        this.#pendingFetch = null;
        const html = await htmlPromise;
        const gridEl = html?.querySelector('[data-product-grid-content]');
        this.#debug('Upsell page fetched', {
          productUrl,
          hasHtml: Boolean(html),
          hasProductGrid: Boolean(gridEl),
          fromCache: false,
        });
        if (gridEl) {
          this.#cachedProductGrid = /** @type {Element} */ (gridEl.cloneNode(true));
          productGrid = /** @type {Element} */ (gridEl.cloneNode(true));
        }
      } else {
        this.#debug('Upsell page served from cache', { productUrl });
      }

      if (productGrid) {
        await this.#updateModal(productGrid, productUrl);
      }
    } catch (error) {
      quickAddDialog?.closeDialog?.();
      throw error;
    } finally {
      quickAddDialog?.setLoadingState?.(false);
      this.#setButtonLoadingState(triggerButton, false);
    }
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Set 5-second timeout on fetch
    const timeoutId = setTimeout(() => {
      this.#abortController?.abort();
      this.#debug('Fetch timeout: product page took longer than 5 seconds', { url: productPageUrl });
    }, 5000);

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.#debug('Fetch aborted', { url: productPageUrl, message: error.message });
        return null;
      }
      this.#debug('Fetch error', { url: productPageUrl, message: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      clearTimeout(timeoutId);
      this.#abortController = null;
    }
  }

  /**
   * @param {Element} productGrid
   * @param {string} productUrl
   */
  async #updateModal(productGrid, productUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const buyButtonsBlock = productGrid.querySelector('.buy-buttons-block');
      const productPrice = productGrid.querySelector('product-price');
      const sourceTitle = productGrid.querySelector('.view-product-title a, .product-title a, .product-title');

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      const productTitle = document.createElement('a');
      productTitle.href = productUrl;
      productTitle.textContent = sourceTitle?.textContent?.trim() || '';
      productHeader.appendChild(productTitle);

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      if (buyButtonsBlock) {
        productGrid.appendChild(buyButtonsBlock);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    this.#enhanceModal(modalContent);
  }

  /** @param {HTMLElement} modalContent */
  #enhanceModal(modalContent) {
    this.#badgeCanvasRenderGeneration += 1;
    // Reset abort signal before attaching Step 1 / canvas listeners so reopen works.
    this.#attachSubmitInterceptor(modalContent);
    this.#injectMainProductSummary(modalContent);
    this.#limitPopupSlides(modalContent);
    this.#filterSizes(modalContent);
    this.#syncUpsellColorSelection(modalContent);
    this.#selectedUpsellVariantId = this.#resolveUpsellVariantId(modalContent);
    this.#injectLineProperties(modalContent);
    this.#injectRichText(modalContent);
    this.#disableNativeSubmitHandling(modalContent);
    this.#upsellPrice = this.#resolveUpsellVariantPrice(modalContent);
    this.#hideUpsellInlinePrice(modalContent);
    if (this.dataset.showPriceSummary !== 'false') {
      this.#injectPriceSummary(modalContent);
    }
    this.#injectButtonTotal(modalContent);
    this.#injectBadgeCanvas(modalContent);
    this.#attachVariantPriceListener(modalContent);
    this.#repositionInstallments(modalContent);
    this.#scheduleButtonTotalRefresh(modalContent);
  }

  /**
   * Moves Shop Pay installments under the add-to-cart form in the upsell modal.
   *
   * @param {HTMLElement} modalContent
   */
  #repositionInstallments(modalContent) {
    const installments = modalContent.querySelector('.installments');
    const productForm = modalContent.querySelector('product-form-component');
    if (!(installments instanceof HTMLElement) || !(productForm instanceof HTMLElement)) return;

    productForm.insertAdjacentElement('afterend', installments);
  }

  /** @param {HTMLElement} modalContent */
  #scheduleButtonTotalRefresh(modalContent) {
    queueMicrotask(() => this.#injectButtonTotal(modalContent));
    requestAnimationFrame(() => this.#injectButtonTotal(modalContent));
  }

  /** @param {HTMLElement} modalContent */
  #limitPopupSlides(modalContent) {
    const slides = [...modalContent.querySelectorAll('.product-information__media slideshow-slide')];
    if (slides.length <= 1) return;

    for (const [index, slide] of slides.entries()) {
      if (!(slide instanceof HTMLElement)) continue;

      const isFirst = index === 0;
      slide.hidden = !isFirst;
      slide.setAttribute('aria-hidden', isFirst ? 'false' : 'true');
      slide.style.display = isFirst ? '' : 'none';
    }
  }

  /** @param {HTMLElement} modalContent */
  #filterSizes(modalContent) {
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) {
      this.#debug('filterSizes: no variant-picker found in modal', { modalContent });
      return;
    }

    const sizeFieldsets = [...variantPicker.querySelectorAll('fieldset.variant-option--buttons')];
    if (sizeFieldsets.length === 0) {
      this.#debug('filterSizes: no size fieldsets found', { variantPickerOuter: variantPicker.outerHTML?.slice?.(0, 240) });
      return;
    }

    const mappedSizeValues = this.#resolveMappedSizeValues();
    if (mappedSizeValues.size === 0) {
      this.#debug('filterSizes: mapped size values empty', { mainSizeValue: this.#mainSizeValue, dataSizeMappings: this.dataset.sizeMappings });
      return;
    }

    const targetFieldset = this.#findSizeFieldset(sizeFieldsets) || sizeFieldsets[0];
    if (!targetFieldset) return;
    if (!(targetFieldset instanceof HTMLElement)) return;
    const labels = [...targetFieldset.querySelectorAll('label')];
    const sizeSummaryClass = 'upsell-size-summary';

    let visibleCount = 0;
    let selectedLabelText = '';
    /** @type {HTMLLabelElement[]} */
    const elementsToHide = [];
    /** @type {HTMLLabelElement[]} */
    const elementsToShow = [];

    // Collect read operations
    for (const label of labels) {
      if (!(label instanceof HTMLLabelElement)) continue;
      const input = label.querySelector('input[type="radio"]');
      if (!(input instanceof HTMLInputElement)) continue;

      const optionValues = this.#resolveNormalizedOptionValues(label, input);
      const isVisible = [...optionValues].some((value) => mappedSizeValues.has(value));

      if (!isVisible) {
        elementsToHide.push(label);
      } else {
        elementsToShow.push(label);
        visibleCount += 1;

        if (!selectedLabelText) {
          selectedLabelText = this.#resolveSizeOptionLabel(label, input);
        }
      }
    }

    // Batch all DOM writes in requestAnimationFrame
    requestAnimationFrame(() => {
      // Reset fieldset visibility
      targetFieldset.hidden = false;
      targetFieldset.removeAttribute('aria-hidden');
      targetFieldset.style.display = '';

      // Update label visibility
      for (const label of elementsToHide) {
        label.hidden = true;
      }
      for (const label of elementsToShow) {
        label.hidden = false;
      }

      // Handle fallback/summary messaging
      const fallbackClass = 'upsell-size-fallback';
      const existingFallback = targetFieldset.parentElement?.querySelector(`.${fallbackClass}`);
      const existingSummary = targetFieldset.parentElement?.querySelector(`.${sizeSummaryClass}`);

      if (visibleCount === 0) {
        existingSummary?.remove();
        if (!existingFallback) {
          const fallback = document.createElement('p');
          fallback.className = fallbackClass;
          fallback.textContent = this.dataset.sizeUnavailableLabel || 'No compatible size available for this selection.';
          targetFieldset.insertAdjacentElement('afterend', fallback);
        }
        targetFieldset.hidden = true;
        targetFieldset.setAttribute('aria-hidden', 'true');
        targetFieldset.style.display = 'none';
        return;
      }

      existingFallback?.remove();

      // Auto-select first available mapped size
      const mappedOrder = [...mappedSizeValues.values()];
      let mappedInput = null;

      for (const mappedValue of mappedOrder) {
        const candidate = elementsToShow
          .map((label) => label.querySelector('input[type="radio"]'))
          .find((input) => {
            if (!(input instanceof HTMLInputElement)) return false;

            const optionValues = this.#resolveNormalizedOptionValues(input.closest('label'), input);
            return optionValues.has(mappedValue);
          });

        if (candidate instanceof HTMLInputElement) {
          mappedInput = candidate;
          break;
        }
      }

      const checkedInput = targetFieldset.querySelector('input[type="radio"]:checked');
      const firstVisibleInput = mappedInput
        || elementsToShow.map((label) => label.querySelector('input[type="radio"]')).find((input) => input instanceof HTMLInputElement);

      let checkedMatchesMapped = false;
      if (checkedInput instanceof HTMLInputElement) {
        const checkedValues = this.#resolveNormalizedOptionValues(checkedInput.closest('label'), checkedInput);
        checkedMatchesMapped = [...checkedValues].some((value) => mappedSizeValues.has(value));
      }

      if (firstVisibleInput instanceof HTMLInputElement) {
        const shouldSelectMappedInput =
          !(checkedInput instanceof HTMLInputElement)
          || checkedInput.closest('label')?.hidden
          || !checkedMatchesMapped
          || checkedInput !== firstVisibleInput;

        if (shouldSelectMappedInput) {
          firstVisibleInput.checked = true;
          firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Update selected size summary
      const currentInput = targetFieldset.querySelector('input[type="radio"]:checked');
      if (currentInput instanceof HTMLInputElement) {
        const currentLabel = currentInput.closest('label');
        if (currentLabel instanceof HTMLLabelElement) {
          selectedLabelText = this.#resolveSizeOptionLabel(currentLabel, currentInput);
        }
      }

      if (selectedLabelText) {
        const sizeSummary = existingSummary instanceof HTMLElement ? existingSummary : document.createElement('p');
        sizeSummary.className = sizeSummaryClass;
        sizeSummary.textContent = `${this.dataset.sizeLabel || 'Size'}: ${selectedLabelText}`;

        if (!sizeSummary.isConnected) {
          targetFieldset.insertAdjacentElement('afterend', sizeSummary);
        }
      }

      targetFieldset.hidden = true;
      targetFieldset.setAttribute('aria-hidden', 'true');
      targetFieldset.style.display = 'none';
    });

    this.#debug('Filtering upsell sizes', {
      mappedSizeValues: [...mappedSizeValues],
      visibleCount,
      allOptions: labels
        .map((label) => label.querySelector('input[type="radio"]'))
        .filter((input) => input instanceof HTMLInputElement)
        .map((input) => ({
          value: input.value,
          normalized: this.#normalizeSizeValue(input.value || ''),
        })),
    });
  }

  /** @param {HTMLElement} modalContent */
  #injectLineProperties(modalContent) {
    const existingProps = modalContent.querySelector('upsell-line-properties');
    const preservedValues = {
      customMessage: '',
      secondaryText: '',
    };

    if (existingProps instanceof HTMLElement) {
      const customInput = existingProps.querySelector('input[data-upsell-field="custom-message"]');
      const secondaryInput = existingProps.querySelector('input[data-upsell-field="secondary-text"]');
      if (customInput instanceof HTMLInputElement) preservedValues.customMessage = customInput.value;
      if (secondaryInput instanceof HTMLInputElement) preservedValues.secondaryText = secondaryInput.value;
      existingProps.remove();
    }

    const productForm = modalContent.querySelector('product-form-component form');
    if (!productForm) return;

    const lineProps = /** @type {UpsellLineProperties} */ (document.createElement('upsell-line-properties'));
    lineProps.dataset.customMessageLabel = this.dataset.customMessageLabel || 'Custom Message';
    lineProps.dataset.secondaryTextLabel = this.dataset.secondaryTextLabel || 'Secondary Text';
    lineProps.dataset.customMessageRequired = this.#isCustomMessageRequired() ? 'true' : 'false';

    if (!productForm.id) {
      productForm.id = `upsell-form-${Math.random().toString(36).slice(2, 10)}`;
    }
    lineProps.setFormId(productForm.id);

    const buyButtonsBlock = this.#resolveModalBuyButtonsBlock(modalContent);
    if (buyButtonsBlock instanceof HTMLElement) {
      buyButtonsBlock.insertAdjacentElement('beforebegin', lineProps);
    } else {
      productForm.prepend(lineProps);
    }

    queueMicrotask(() => {
      const customInput = lineProps.querySelector('input[data-upsell-field="custom-message"]');
      const secondaryInput = lineProps.querySelector('input[data-upsell-field="secondary-text"]');
      if (customInput instanceof HTMLInputElement) customInput.value = preservedValues.customMessage;
      if (secondaryInput instanceof HTMLInputElement) secondaryInput.value = preservedValues.secondaryText;

      if (customInput instanceof HTMLInputElement) {
        customInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  /** @param {HTMLElement} modalContent */
  #injectRichText(modalContent) {
    modalContent.querySelector('[data-upsell-rich-text]')?.remove();

    const richTextTemplate = this.querySelector('template[data-rich-text]');
    if (!(richTextTemplate instanceof HTMLTemplateElement)) return;

    const target = modalContent.querySelector('upsell-line-properties') || modalContent.querySelector('variant-picker');
    if (!target) return;

    const richTextContainer = document.createElement('div');
    richTextContainer.classList.add('quick-add-visible');
    richTextContainer.setAttribute('data-upsell-rich-text', '');
    
    // Sanitize rich text: clone template content and strip script tags + event handlers
    const clonedContent = /** @type {DocumentFragment} */ (richTextTemplate.content.cloneNode(true));
    const scripts = clonedContent.querySelectorAll('script');
    scripts.forEach((script) => script.remove());
    
    // Remove event handler attributes from all elements
    const allElements = clonedContent.querySelectorAll('*');
    for (const element of allElements) {
      if (!(element instanceof Element)) continue;
      const attributes = [...element.attributes];
      for (const attr of attributes) {
        if (attr.name.startsWith('on')) {
          element.removeAttribute(attr.name);
        }
      }
    }
    
    richTextContainer.appendChild(clonedContent);
    target.insertAdjacentElement('afterend', richTextContainer);
    this.#debug('Injected rich text with sanitization', { hasScripts: scripts.length > 0 });
  }

  /** @param {HTMLElement} modalContent */
  #injectMainProductSummary(modalContent) {
    this.#captureMainProductState();
    modalContent.querySelector('[data-main-config-summary]')?.remove();
    modalContent.querySelectorAll('[data-upsell-step]').forEach((el) => el.remove());
    modalContent.querySelectorAll('[data-upsell-popup-title]').forEach((el) => el.remove());

    const target =
      modalContent.querySelector('.view-product-title')
      || modalContent.querySelector('variant-picker')
      || modalContent.querySelector('product-form-component')
      || modalContent.firstElementChild;
    if (!(target instanceof Element)) return;

    const context = this.#mainProductContext;
    const hasContent = Boolean(context.title || context.imageSrc || context.selectedOptions.length > 0);
    if (!hasContent) return;

    const summary = document.createElement('div');
    summary.className = 'upsell-main-config quick-add-visible';
    summary.setAttribute('data-main-config-summary', '');
    summary.setAttribute(
      'aria-label',
      context.title ? `Current product configuration for ${context.title}` : 'Current product configuration'
    );

    const content = document.createElement('div');
    content.className = 'upsell-main-config__content';

    const body = document.createElement('div');
    body.className = 'upsell-main-config__body';

    if (context.title) {
      const title = document.createElement('div');
      title.className = 'upsell-main-config__title';
      title.textContent = context.title;
      body.appendChild(title);
    }

    const selectedVariants = this.#resolveSelectedVariantsText(context.selectedOptions);
    if (selectedVariants) {
      const variants = document.createElement('p');
      variants.className = 'upsell-main-config__variants';
      variants.textContent = selectedVariants;
      body.appendChild(variants);
    }

    const hasColorSwatches = this.#injectMainProductColorSwatches(body, modalContent);

    const editAction = document.createElement('button');
    editAction.type = 'button';
    editAction.className = 'button button-unstyled upsell-main-config__edit';
    editAction.textContent = this.dataset.mainConfigEditLabel || 'Edit main product';
    editAction.addEventListener(
      'click',
      () => {
        const dialogComponent = document.getElementById('quick-add-dialog');
        if (dialogComponent instanceof HTMLElement) {
          /** @type {QuickAddDialogElement} */ (dialogComponent).closeDialog?.();
        }
      },
      { signal: this.#submitAbortController?.signal }
    );
    body.appendChild(editAction);

    content.appendChild(body);

    if (!hasColorSwatches && context.imageSrc) {
      const image = document.createElement('img');
      image.className = 'upsell-main-config__image';
      image.src = context.imageSrc;
      image.alt = context.imageAlt || context.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      content.appendChild(image);
    } else if (hasColorSwatches) {
      content.classList.add('upsell-main-config__content--swatches-only');
    }

    summary.appendChild(content);
    target.insertAdjacentElement('beforebegin', summary);

    const popupTitleLabel = this.dataset.popupTitleLabel || 'Choose Your Combination';
    const step1Label = this.dataset.step1Label || 'Step 1: Your Product';
    const step2Label = this.dataset.step2Label || 'Step 2: Customize';
    const priceLabel = this.#resolveMainPriceLabel();
    const upsellPriceLabel = this.#resolveUpsellPriceLabel();

    const popupTitleEl = document.createElement('div');
    popupTitleEl.className = 'upsell-popup-title quick-add-visible';
    popupTitleEl.setAttribute('data-upsell-popup-title', '');
    popupTitleEl.textContent = popupTitleLabel;
    summary.insertAdjacentElement('beforebegin', popupTitleEl);

    const step1El = this.#createStepLabel('1', step1Label, priceLabel);
    summary.insertAdjacentElement('beforebegin', step1El);

    const step2El = this.#createStepLabel('2', step2Label, upsellPriceLabel);
    target.insertAdjacentElement('beforebegin', step2El);

    this.#debug('Injected main product summary', {
      context,
      selectedVariants,
      priceLabel,
      upsellPriceLabel,
    });
  }

  /**
   * @param {HTMLElement} body
   * @param {HTMLElement} modalContent
   * @returns {boolean} Whether color swatches were rendered
   */
  #injectMainProductColorSwatches(body, modalContent) {
    const colorOptions = this.#resolveMainColorOptions();
    if (colorOptions.length === 0) return false;

    const colorLabel = this.dataset.mainConfigColorLabel || 'Color';
    const fieldset = document.createElement('fieldset');
    fieldset.className =
      'variant-option variant-option--buttons variant-option--swatches upsell-main-config__colors';
    fieldset.setAttribute('role', 'radiogroup');
    fieldset.setAttribute('aria-label', colorLabel);

    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = colorLabel;
    fieldset.appendChild(legend);

    for (const option of colorOptions) {
      const label = document.createElement('label');
      label.className =
        'variant-option__button-label variant-option__button-label--has-swatch upsell-main-config__color-choice';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'upsell-main-color';
      input.value = option.value;
      input.setAttribute('aria-label', option.label);
      input.checked = option.selected;
      input.dataset.currentChecked = option.selected ? 'true' : 'false';
      input.dataset.optionAvailable = option.available ? 'true' : 'false';
      if (!option.available) {
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
      }

      const swatch = document.createElement('span');
      const hasSwatch = Boolean(option.swatchBackground);
      swatch.className = hasSwatch
        ? 'swatch swatch--unscaled'
        : 'swatch swatch--unscaled swatch--unavailable';
      if (option.swatchBackground) {
        swatch.style.setProperty('--swatch-background', option.swatchBackground);
      }

      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = option.label;

      label.appendChild(input);
      label.appendChild(swatch);
      label.appendChild(hiddenLabel);
      fieldset.appendChild(label);
    }

    fieldset.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.name !== 'upsell-main-color') return;
        void this.#handleMainColorSwatchChange(target.value, modalContent);
      },
      { signal: this.#submitAbortController?.signal }
    );

    body.appendChild(fieldset);
    return true;
  }

  /**
   * @returns {Array<{ value: string, label: string, selected: boolean, available: boolean, swatchBackground: string }>}
   */
  #resolveMainColorOptions() {
    const colorFieldset = this.#resolveMainColorFieldset();
    if (!(colorFieldset instanceof HTMLFieldSetElement)) return [];

    /** @type {Array<{ value: string, label: string, selected: boolean, available: boolean, swatchBackground: string }>} */
    const options = [];

    for (const input of colorFieldset.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const labelEl = input.closest('label');
      const label = this.#resolveSizeOptionLabel(labelEl, input) || input.value?.trim() || '';
      const value = input.value?.trim() || label;
      if (!value) continue;

      const available = input.dataset.optionAvailable !== 'false' && !input.disabled;
      const swatchEl = labelEl?.querySelector('.swatch');
      let swatchBackground = '';
      if (swatchEl instanceof HTMLElement) {
        swatchBackground =
          swatchEl.style.getPropertyValue('--swatch-background').trim()
          || getComputedStyle(swatchEl).getPropertyValue('--swatch-background').trim();
      }

      options.push({
        value,
        label: label || value,
        selected: input.checked || input.dataset.currentChecked === 'true',
        available,
        swatchBackground,
      });
    }

    return options;
  }

  /**
   * @param {Element | null} [variantPicker]
   * @returns {HTMLFieldSetElement | null}
   */
  #resolveMainColorFieldset(variantPicker = null) {
    const picker =
      variantPicker
      || this.#resolveMainVariantPicker(this.#resolveMainProductForm());
    if (!(picker instanceof HTMLElement)) return null;

    for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const optionName =
        fieldset.querySelector('legend')?.textContent
        || fieldset.getAttribute('data-option-name')
        || fieldset.getAttribute('data-fieldset-option-name')
        || fieldset.getAttribute('option')
        || '';

      const normalizedOptionName = this.#normalizeOptionName(optionName);
      if (normalizedOptionName.includes('color') || normalizedOptionName.includes('colour')) {
        return fieldset;
      }
    }

    return null;
  }

  /**
   * @param {string} colorValue
   * @param {HTMLElement} modalContent
   * @returns {Promise<void>}
   */
  async #handleMainColorSwatchChange(colorValue, modalContent) {
    if (this.#mainColorUpdateInFlight) return;

    const normalizedTarget = this.#normalizeSizeValue(colorValue);
    if (!normalizedTarget) return;

    const currentColor = this.#resolveSelectedMainColor();
    if (currentColor && this.#normalizeSizeValue(currentColor) === normalizedTarget) return;

    const mainForm = this.#resolveMainProductForm();
    const mainPicker = this.#resolveMainVariantPicker(mainForm);
    const colorFieldset = this.#resolveMainColorFieldset(mainPicker);
    if (!(colorFieldset instanceof HTMLFieldSetElement) || !(mainPicker instanceof HTMLElement)) return;

    const matchingRadio = [...colorFieldset.querySelectorAll('input[type="radio"]')].find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      if (input.disabled || input.dataset.optionAvailable === 'false') return false;

      const optionValues = this.#resolveNormalizedOptionValues(input.closest('label'), input);
      return optionValues.has(normalizedTarget);
    });

    if (!(matchingRadio instanceof HTMLInputElement)) {
      this.#debug('Main color swatch: no matching PDP radio', { colorValue });
      return;
    }

    if (matchingRadio.checked) {
      this.#refreshAfterMainColorChange(modalContent);
      return;
    }

    this.#mainColorUpdateInFlight = true;

    try {
      const selectPromise = new Promise((resolve, reject) => {
        const onSelect = (event) => {
          const productSelectEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);
          void productSelectEvent.promise.then(resolve).catch(reject);
        };

        mainPicker.addEventListener(StandardEvents.productSelect, onSelect, {
          once: true,
          signal: this.#submitAbortController?.signal,
        });
      });

      matchingRadio.checked = true;
      matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));

      await Promise.race([
        selectPromise,
        new Promise((resolve) => {
          setTimeout(resolve, 2000);
        }),
      ]);
      this.#refreshAfterMainColorChange(modalContent);
    } catch (error) {
      this.#debug('Main color swatch update failed', { colorValue, error });
      this.#refreshAfterMainColorChange(modalContent);
    } finally {
      this.#mainColorUpdateInFlight = false;
    }
  }

  /** @param {HTMLElement} modalContent */
  #refreshAfterMainColorChange(modalContent) {
    if (!(modalContent instanceof HTMLElement) || !modalContent.isConnected) return;

    this.#captureMainProductState();
    this.#injectMainProductSummary(modalContent);
    this.#syncUpsellColorSelection(modalContent);
    this.#filterSizes(modalContent);
    this.#hideUpsellInlinePrice(modalContent);

    if (this.dataset.showPriceSummary !== 'false') {
      this.#injectPriceSummary(modalContent);
    }

    this.#injectButtonTotal(modalContent);
    this.#scheduleButtonTotalRefresh(modalContent);
    this.#badgeCanvasRenderFn?.();
  }

  /** @param {HTMLElement} modalContent */
  #syncUpsellColorSelection(modalContent) {
    const selectedMainColor = this.#resolveSelectedMainColor();
    if (!selectedMainColor) return;

    const variantPicker = modalContent.querySelector('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return;

    const colorFieldset = [...variantPicker.querySelectorAll('fieldset.variant-option')].find((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;

      const optionName =
        fieldset.querySelector('legend')?.textContent
        || fieldset.getAttribute('data-option-name')
        || fieldset.getAttribute('option')
        || '';

      const normalizedOptionName = this.#normalizeOptionName(optionName);
      return normalizedOptionName.includes('color') || normalizedOptionName.includes('colour');
    });

    if (!(colorFieldset instanceof HTMLFieldSetElement)) return;

    const normalizedMainColor = this.#normalizeSizeValue(selectedMainColor);
    const tokenize = /** @param {string | null | undefined} value */ (value) =>
      String(value || '')
        .toLowerCase()
        .split(/[^a-z0-9]+/gu)
        .map((part) => part.trim())
        .filter((part) => part.length >= 3);
    const mainColorTokens = new Set(tokenize(selectedMainColor));

    const hasTokenMatch = /** @param {Iterable<string>} candidates */ (candidates) => {
      if (mainColorTokens.size === 0) return false;
      for (const candidate of candidates) {
        for (const token of tokenize(candidate)) {
          if (mainColorTokens.has(token)) return true;
        }
      }
      return false;
    };

    const radioInputs = [...colorFieldset.querySelectorAll('input[type="radio"]')].filter(
      (input) => input instanceof HTMLInputElement
    );
    const matchingRadio = radioInputs.find((input) => {
      const optionValues = this.#resolveNormalizedOptionValues(input.closest('label'), input);
      return optionValues.has(normalizedMainColor);
    });

    const partialMatchingRadio = matchingRadio || radioInputs.find((input) => {
      const label = input.closest('label');
      const optionValues = this.#resolveNormalizedOptionValues(label, input);
      const candidates = [
        input.value,
        label instanceof HTMLLabelElement ? this.#resolveSizeOptionLabel(label, input) : '',
        ...optionValues,
      ];
      return hasTokenMatch(candidates);
    });

    if (partialMatchingRadio instanceof HTMLInputElement) {
      if (!partialMatchingRadio.checked) {
        partialMatchingRadio.checked = true;
        partialMatchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const select = colorFieldset.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return;

    const matchingOption = [...select.options].find((option) => {
      const normalizedValue = this.#normalizeSizeValue(option.value || '');
      const normalizedLabel = this.#normalizeSizeValue(option.textContent?.trim() || '');
      return normalizedValue === normalizedMainColor || normalizedLabel === normalizedMainColor;
    });

    const partialMatchingOption = matchingOption || [...select.options].find((option) => {
      const candidates = [option.value || '', option.textContent?.trim() || ''];
      return hasTokenMatch(candidates);
    });

    if (!partialMatchingOption || select.value === partialMatchingOption.value) return;

    select.value = partialMatchingOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeOptionName(value) {
    return String(value || '').trim().toLowerCase().replace(/[:\s]+$/u, '');
  }

  /** @returns {string | null} */
  #resolveSelectedMainColor() {
    const selectedColor = this.#mainProductContext.selectedOptions.find((option) => {
      const normalizedOptionName = this.#normalizeOptionName(option?.name || '');
      return normalizedOptionName.includes('color') || normalizedOptionName.includes('colour');
    });

    return selectedColor?.value?.trim() || null;
  }

  /**
   * @param {string} stepNumber
   * @param {string} title
   * @param {string | null} priceLabel
   * @returns {HTMLDivElement}
   */
  #createStepLabel(stepNumber, title, priceLabel) {
    const stepEl = document.createElement('div');
    stepEl.className = 'upsell-step-label quick-add-visible';
    stepEl.setAttribute('data-upsell-step', stepNumber);

    const content = document.createElement('div');
    content.className = 'upsell-step-label-content product-badges__badge product-badges__badge--rectangle';

    const titleSpan = document.createElement('span');
    titleSpan.className = 'upsell-step-label-content__title';
    titleSpan.textContent = title;
    content.appendChild(titleSpan);

    if (priceLabel) {
      const priceSpan = document.createElement('span');
      priceSpan.className = 'upsell-step-label-content__price';
      priceSpan.textContent = priceLabel;
      content.appendChild(priceSpan);
    }

    stepEl.appendChild(content);
    return stepEl;
  }

  /** @param {HTMLElement} modalContent */
  #injectPriceSummary(modalContent) {
    const summaryContainer = this.#resolvePriceSummaryContainer(modalContent);
    if (!(summaryContainer instanceof HTMLElement)) return;

    const existingSummary = summaryContainer.querySelector('.upsell-price-summary');
    existingSummary?.remove();

    const mainPriceLabel = this.#resolveMainPriceLabel();
    const upsellPriceLabel = this.#resolveUpsellPriceLabel();
    if (!mainPriceLabel && !upsellPriceLabel) return;

    const summary = document.createElement('div');
    summary.className = 'upsell-price-summary quick-add-visible';

    const mainTitle = this.#mainProductContext.title || 'Main product';
    const upsellTitle = this.#resolveUpsellProductTitle(modalContent) || 'Badge';

    if (mainPriceLabel) {
      const mainLine = document.createElement('div');
      mainLine.className = 'upsell-price-summary__item';
      mainLine.dataset.minorUnits = String(Math.max(0, this.#mainPrice * this.#mainQty));

      const titleSpan = document.createElement('span');
      titleSpan.textContent = mainTitle;
      titleSpan.className = 'upsell-price-summary__label';

      const valueSpan = document.createElement('span');
      valueSpan.textContent = mainPriceLabel;
      valueSpan.className = 'upsell-price-summary__value';

      mainLine.append(titleSpan, valueSpan);
      summary.appendChild(mainLine);
    }

    if (upsellPriceLabel) {
      const upsellLine = document.createElement('div');
      upsellLine.className = 'upsell-price-summary__item';
      upsellLine.dataset.minorUnits = String(Math.max(0, this.#upsellPrice));

      const titleSpan = document.createElement('span');
      titleSpan.textContent = upsellTitle;
      titleSpan.className = 'upsell-price-summary__label';

      const valueSpan = document.createElement('span');
      valueSpan.textContent = `+ ${upsellPriceLabel}`;
      valueSpan.className = 'upsell-price-summary__value';

      upsellLine.append(titleSpan, valueSpan);
      summary.appendChild(upsellLine);
    }

    summaryContainer.prepend(summary);
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {HTMLElement | null}
   */
  #resolvePriceSummaryContainer(modalContent) {
    const buyButtonsBlock = this.#resolveModalBuyButtonsBlock(modalContent);
    if (buyButtonsBlock instanceof HTMLElement) return buyButtonsBlock;

    const addToCartComponent = modalContent.querySelector('product-form-component add-to-cart-component, add-to-cart-component');
    if (addToCartComponent instanceof HTMLElement) return addToCartComponent;

    const productForm = modalContent.querySelector('product-form-component form, form[action*="/cart/add"]');
    if (productForm instanceof HTMLElement) return productForm;

    return null;
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {HTMLElement | null}
   */
  #resolveModalBuyButtonsBlock(modalContent) {
    const inProductDetails = modalContent.querySelector('.product-details :is(.buy-buttons-block)');
    if (inProductDetails instanceof HTMLElement) return inProductDetails;

    return null;
  }

  /** @param {HTMLElement} modalContent */
  #injectButtonTotal(modalContent) {
    const submitButton =
      modalContent.querySelector('product-form-component add-to-cart-component button[type="submit"]') ||
      modalContent.querySelector('add-to-cart-component button[type="submit"]') ||
      modalContent.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    const addToCartText = submitButton.querySelector('.add-to-cart-text__content');
    const existingPriceNode = submitButton.querySelector('.upsell-trigger__combined-price');
    existingPriceNode?.remove();

    const summaryTotal = this.#resolveSummaryTotalMinorUnits(modalContent);
    const combinedPriceLabel =
      summaryTotal > 0
        ? this.#formatPrice(summaryTotal)
        : this.#resolveCombinedPriceLabel(modalContent);
    if (!combinedPriceLabel) return;

    const priceNode = document.createElement('span');
    priceNode.className = 'upsell-trigger__combined-price';
    priceNode.textContent = ` - ${combinedPriceLabel}`;

    if (addToCartText instanceof HTMLElement) {
      addToCartText.insertAdjacentElement('afterend', priceNode);
      return;
    }

    submitButton.appendChild(priceNode);
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {number}
   */
  #resolveSummaryTotalMinorUnits(modalContent) {
    const summaryItems = modalContent.querySelectorAll('.upsell-price-summary__item[data-minor-units]');
    if (summaryItems.length === 0) return 0;

    let total = 0;
    for (const item of summaryItems) {
      if (!(item instanceof HTMLElement)) continue;
      const lineMinorUnits = Number(item.dataset.minorUnits || 0);
      if (Number.isFinite(lineMinorUnits) && lineMinorUnits > 0) {
        total += lineMinorUnits;
      }
    }

    return total;
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {string | null}
   */
  #resolveUpsellProductTitle(modalContent) {
    const titleElement =
      modalContent.querySelector('.view-product-title a, .view-product-title, .product-title a, .product-title, h1, h2');
    return titleElement?.textContent?.trim() || null;
  }

  /** @param {HTMLElement} modalContent */
  #attachVariantPriceListener(modalContent) {
    modalContent.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        const productSelectEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);

        // Cancel previous pending variant update (debounce)
        if (this.#variantUpdateTimeoutId !== null) {
          clearTimeout(this.#variantUpdateTimeoutId);
          this.#variantUpdateTimeoutId = null;
        }

        // Debounce variant updates by 100ms to prevent cascade of re-renders
        this.#variantUpdateTimeoutId = setTimeout(() => {
          void productSelectEvent.promise
            .then((result) => {
              const resource = result?.detail?.resource;
              const variant = resource ?? result?.variant;

              if (variant && variant.id !== undefined && variant.id !== null) {
                this.#selectedUpsellVariantId = String(variant.id);
              }

              if (resource && typeof resource.price === 'number') {
                this.#upsellPrice = resource.price;
              } else if (variant?.price?.amount != null) {
                // Standard event price is a MoneyV2 string in major units; convert to minor units.
                const amount = Number(variant.price.amount);
                this.#upsellPrice = Number.isFinite(amount) ? Math.round(amount * 100) : this.#resolveUpsellVariantPrice(modalContent);
              } else {
                this.#upsellPrice = this.#resolveUpsellVariantPrice(modalContent);
              }

              this.#debug('Upsell variant updated', {
                variantId: variant?.id,
                variantPrice: resource?.price ?? variant?.price,
                resolvedUpsellPrice: this.#upsellPrice,
                selectedUpsellVariantId: this.#selectedUpsellVariantId,
              });

              this.#limitPopupSlides(modalContent);
              this.#injectMainProductSummary(modalContent);
              this.#filterSizes(modalContent);
              this.#injectLineProperties(modalContent);
              this.#injectRichText(modalContent);
              this.#hideUpsellInlinePrice(modalContent);
              this.#injectPriceSummary(modalContent);
              this.#injectButtonTotal(modalContent);
              this.#scheduleButtonTotalRefresh(modalContent);
              this.#badgeCanvasRenderFn?.();
            })
            .catch(() => {
              // Ignore aborted / failed product selects while the modal is open.
            })
            .finally(() => {
              this.#variantUpdateTimeoutId = null;
            });
        }, 100);
      },
      { signal: this.#submitAbortController?.signal }
    );
  }

  /**
   * Injects a canvas element into the badge product media area and renders
   * the personalised badge preview: overlay background image + label image
   * positioned at the green dot + custom text on top.
   *
   * @param {HTMLElement} modalContent
   */
  #injectBadgeCanvas(modalContent) {
    const overlayImages = this.#parseBadgeImageData('overlayImages');
    const labelImages = this.#parseBadgeImageData('labelImages');
    if (!overlayImages.length && !labelImages.length) return;

    if (this.dataset.overlayFallbackUsed === 'true') {
      this.#debug('Overlay metafield fallback is active — preview may use upsell product overlays', {
        overlayCount: overlayImages.length,
      });
    }

    if (this.dataset.labelFallbackUsed === 'true') {
      this.#debug('Label metafield fallback is active — preview may use page product labels', {
        labelCount: labelImages.length,
      });
    }

    const mediaContainer = modalContent.querySelector('.product-information__media');
    if (!(mediaContainer instanceof HTMLElement)) return;

    mediaContainer.querySelector('.upsell-badge-canvas')?.remove();
    mediaContainer.classList.remove('product-information__media--badge-canvas');

    const canvas = document.createElement('canvas');
    canvas.className = 'upsell-badge-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    mediaContainer.appendChild(canvas);

    const render = () => {
      void this.#updateBadgeCanvasPreview(canvas, mediaContainer, modalContent, overlayImages, labelImages);
    };

    this.#badgeCanvasRenderFn = render;
    render();

    modalContent.addEventListener(
      'input',
      (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.dataset.upsellField) return;
        render();
      },
      { signal: this.#submitAbortController?.signal }
    );
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {HTMLElement} mediaContainer
   * @param {HTMLElement} modalContent
   * @param {Array<{ c: string, u: string }>} overlayImages
   * @param {Array<{ c: string, u: string }>} labelImages
   * @returns {Promise<void>}
   */
  async #updateBadgeCanvasPreview(canvas, mediaContainer, modalContent, overlayImages, labelImages) {
    const generation = ++this.#badgeCanvasRenderGeneration;
    const mainColor = this.#resolveSelectedMainColor() || '';
    const badgeColor = this.#resolveSelectedBadgeColor(modalContent) || mainColor || '';
    const overlayUrl = this.#matchImageByColor(overlayImages, mainColor);
    const matchedOverlayColor =
      (overlayUrl && overlayImages.find((image) => image.u === overlayUrl)?.c) || mainColor;
    const labelUrl = this.#matchImageByColor(labelImages, badgeColor);

    if (!overlayUrl) {
      if (generation === this.#badgeCanvasRenderGeneration) {
        mediaContainer.classList.remove('product-information__media--badge-canvas');
      }
      this.#debug('Badge canvas overlay not matched', { mainColor, overlayCount: overlayImages.length });
      return;
    }

    const primaryInput = /** @type {HTMLInputElement | null} */ (
      modalContent.querySelector('input[data-upsell-field="custom-message"]')
    );
    const secondaryInput = /** @type {HTMLInputElement | null} */ (
      modalContent.querySelector('input[data-upsell-field="secondary-text"]')
    );
    const primaryText = primaryInput?.value?.trim() || '';
    const secondaryText = secondaryInput?.value?.trim() || '';

    const rendered = await this.#renderBadgeCanvas(
      canvas,
      overlayUrl,
      labelUrl,
      primaryText,
      secondaryText,
      matchedOverlayColor
    );

    if (generation !== this.#badgeCanvasRenderGeneration) return;

    mediaContainer.classList.toggle('product-information__media--badge-canvas', rendered);
  }

  /**
   * Renders the badge preview onto a canvas: background overlay image,
   * label image centered at the green dot marker, and text on top.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {string} overlayUrl
   * @param {string | null} labelUrl
   * @param {string} primaryText
   * @param {string} secondaryText
   * @param {string} [overlayColor]
   * @returns {Promise<boolean>}
   */
  async #renderBadgeCanvas(canvas, overlayUrl, labelUrl, primaryText, secondaryText, overlayColor = '') {
    try {
      const [bgImg, labelImg] = await Promise.all([
        loadBadgeImage(overlayUrl, this.#imageCache),
        labelUrl ? loadBadgeImage(labelUrl, this.#imageCache) : Promise.resolve(null),
      ]);

      if (!bgImg) {
        this.#debug('Badge canvas background failed to load', { overlayUrl });
        return false;
      }

      canvas.width = bgImg.naturalWidth;
      canvas.height = bgImg.naturalHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      ctx.drawImage(bgImg, 0, 0);

      if (labelImg) {
        const anchor = resolveLabelAnchor(
          ctx,
          canvas.width,
          canvas.height,
          this.#resolveLabelAnchorConfig(overlayColor)
        );

        const labelScale = this.#resolveLabelScale();
        const labelW = labelImg.naturalWidth * labelScale;
        const labelH = labelImg.naturalHeight * labelScale;
        const startX = anchor.x - labelW / 2.1;
        const startY = anchor.y - labelH / 2.6;

        ctx.drawImage(labelImg, startX, startY, labelW, labelH);

        const text1 = primaryText.trim().toUpperCase();
        const text2 = secondaryText.trim().toUpperCase();
        if (text1 || text2) {
          const fontSize = calcLabelFontSize(labelW, text1, text2);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          const textX = startX + labelW / 2;
          if (text1 && text2) {
            ctx.fillText(text1, textX, startY + labelH * 0.34);
            ctx.fillText(text2, textX, startY + labelH * 0.64);
          } else {
            ctx.fillText(text1 || text2, textX, startY + labelH * 0.5);
          }
        }
      }

      return true;
    } catch (err) {
      this.#debug('Badge canvas render error', err);
      return false;
    }
  }

  /**
   * Parses image data from a data attribute on this element.
   * Returns an array of { c: colorName, u: imageUrl } objects.
   *
   * @param {string} datasetKey  camelCase key (e.g. 'overlayImages')
   * @returns {Array<{ c: string, u: string }>}
   */
  #parseBadgeImageData(datasetKey) {
    const raw = this.dataset[datasetKey] || '';
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Finds the image URL whose color code best matches the given color name.
   * Normalizes both sides to lowercase alphanumeric, then tries exact → prefix → substring.
   *
   * @param {Array<{ c: string, u: string }>} images
   * @param {string} colorName
   * @returns {string | null}
   */
  #matchImageByColor(images, colorName) {
    if (!images.length) return null;

    const normalize = /** @param {string | null | undefined} value */ (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/gu, '');
    const needle = normalize(colorName);

    if (!needle) {
      return images.length === 1 ? images[0].u : null;
    }

    const normalizedImages = images.map((img) => ({
      ...img,
      normalizedColor: normalize(img.c),
    }));

    const exact = normalizedImages.find((img) => img.normalizedColor === needle);
    if (exact) return exact.u;
    const prefix = normalizedImages.find(
      (img) => img.normalizedColor.startsWith(needle) || needle.startsWith(img.normalizedColor)
    );
    if (prefix) return prefix.u;
    const includes = normalizedImages.find(
      (img) => img.normalizedColor.includes(needle) || needle.includes(img.normalizedColor)
    );
    if (includes) return includes.u;

    // Safe fallback when only one image is configured — avoids blank preview without
    // picking the wrong color from a multi-color set (review item #3).
    if (images.length === 1) return images[0].u;

    return null;
  }

  /**
   * @param {string} [overlayColor]
   * @returns {import('./badge-canvas.js').LabelAnchorConfig | null}
   */
  #resolveLabelAnchorConfig(overlayColor = '') {
    let parsedMappings;
    try {
      parsedMappings = JSON.parse(this.dataset.sizeMappings || '{}');
    } catch {
      return null;
    }

    return resolveMappingLabelAnchor(parsedMappings, {
      productHandle: this.#mainProductHandle || '',
      productId: this.#mainProductId || '',
      overlayColor,
    });
  }

  /** @param {HTMLElement} modalContent */
  #resolveSelectedBadgeColor(modalContent) {
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return null;

    const colorFieldset = [...variantPicker.querySelectorAll('fieldset.variant-option')].find((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;
      const optionName =
        fieldset.querySelector('legend')?.textContent ||
        fieldset.getAttribute('data-option-name') ||
        fieldset.getAttribute('option') ||
        '';
      const normalized = this.#normalizeOptionName(optionName);
      return normalized.includes('color') || normalized.includes('colour');
    });

    if (!(colorFieldset instanceof HTMLFieldSetElement)) return null;

    const checkedRadio = colorFieldset.querySelector('input[type="radio"]:checked');
    if (checkedRadio instanceof HTMLInputElement) return checkedRadio.value?.trim() || null;

    const select = colorFieldset.querySelector('select');
    if (select instanceof HTMLSelectElement) return select.value?.trim() || null;

    return null;
  }

  /**
   * Resolves badge preview label scale from size_mappings config.
   *
   * @returns {number}
   */
  #resolveLabelScale() {
    const rawMappings = this.dataset.sizeMappings;
    if (!rawMappings) return 1;

    let parsedMappings;
    try {
      parsedMappings = JSON.parse(rawMappings);
    } catch {
      return 1;
    }

    if (!parsedMappings || typeof parsedMappings !== 'object') return 1;

    const root = /** @type {{ by_handle?: Record<string, unknown>, by_product?: Record<string, unknown> }} */ (parsedMappings);
    const productId = this.#mainProductId ? String(this.#mainProductId) : '';
    const productHandle = this.#mainProductHandle || '';

    /** @param {unknown} value */
    const clampScale = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return 1;
      return Math.min(1.5, Math.max(0.3, num));
    };

    if (root.by_handle && typeof root.by_handle === 'object' && productHandle) {
      const handleEntry = root.by_handle[productHandle];
      if (handleEntry && typeof handleEntry === 'object') {
        const labelScale = /** @type {{ label_scale?: unknown }} */ (handleEntry).label_scale;
        if (labelScale !== undefined && labelScale !== null) {
          return clampScale(labelScale);
        }
      }
    }

    if (root.by_product && typeof root.by_product === 'object' && productId) {
      const productEntry = root.by_product[productId] || root.by_product[Number(productId)];
      if (productEntry && typeof productEntry === 'object') {
        const labelScale = /** @type {{ label_scale?: unknown }} */ (productEntry).label_scale;
        if (labelScale !== undefined && labelScale !== null) {
          return clampScale(labelScale);
        }
      }
    }

    return 1;
  }

  /** @param {HTMLElement} modalContent */
  #attachSubmitInterceptor(modalContent) {
    this.#submitAbortController?.abort();
    this.#submitAbortController = new AbortController();

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof HTMLElement) {
      dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.#submitAbortController?.abort(), {
        once: true,
        signal: this.#submitAbortController.signal,
      });
    }

    modalContent.addEventListener('submit', this.#handleUpsellSubmit, {
      capture: true,
      signal: this.#submitAbortController.signal,
    });
  }

  /** @param {HTMLElement} modalContent */
  #disableNativeSubmitHandling(modalContent) {
    const productFormComponent = modalContent.querySelector('product-form-component[on\\:submit]');
    if (!(productFormComponent instanceof HTMLElement)) return;

    const currentSubmitHandler = productFormComponent.getAttribute('on:submit');
    if (!currentSubmitHandler) return;

    productFormComponent.dataset.upsellOriginalOnSubmit = currentSubmitHandler;
    productFormComponent.removeAttribute('on:submit');
  }

  /** @param {SubmitEvent} event */
  #handleUpsellSubmit = async (event) => {
    const eventTarget = event.target;
    const form =
      eventTarget instanceof HTMLFormElement
        ? eventTarget
        : eventTarget instanceof Element
          ? eventTarget.closest('form')
          : null;
    if (!(form instanceof HTMLFormElement)) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.contains(form)) return;

    if (this.#isSubmittingUpsell) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // Re-capture right before submit so dual add uses the current main selection.
    this.#captureMainProductState();

    const lineProps = /** @type {UpsellLineProperties | null} */ (modalContent.querySelector('upsell-line-properties'));
    lineProps?.clearErrors();

    const customMessage = lineProps?.customMessageValue || '';
    const secondaryText = lineProps?.secondaryTextValue || '';
    const customMessageRequired = this.dataset.customMessageRequired || 'Custom message is required.';
    const customMessageIsRequired = this.#isCustomMessageRequired();

    let hasValidationErrors = false;
    if (customMessageIsRequired && !customMessage) {
      lineProps?.showError(0, customMessageRequired);
      hasValidationErrors = true;
    }
    if (hasValidationErrors) return;

    if (this.#variantUpdateTimeoutId !== null) {
      clearTimeout(this.#variantUpdateTimeoutId);
      this.#variantUpdateTimeoutId = null;
    }

    const upsellVariantId = this.#resolveUpsellVariantId(modalContent, form);

    if (!upsellVariantId || !this.#mainVariantId) {
      this.#debug('Missing variant ids during upsell submit', {
        upsellVariantId,
        cachedSelectedUpsellVariantId: this.#selectedUpsellVariantId,
        mainVariantId: this.#mainVariantId,
      });
      this.#showFormError(form, this.dataset.addError || 'Unable to add upsell item to cart.');
      return;
    }

    const companionItems = this.#collectCompanionCartItems();
    const payload = {
      items: [
        {
          id: Number(this.#mainVariantId),
          quantity: this.#mainQty,
        },
        {
          id: Number(upsellVariantId),
          quantity: 1,
          properties: {
            'Custom Message': customMessage,
            'Secondary Text': secondaryText,
          },
        },
      ],
    };

    this.#mergeCompanionCartItems(payload, companionItems);

    /** @type {{ merchandiseId: string, quantity: number }[]} */
    const eventLines = payload.items.map((item) => ({
      merchandiseId: String(item.id),
      quantity: Number(item.quantity) || 1,
    }));

    this.#debug('Submitting upsell payload', payload);
    this.#isSubmittingUpsell = true;
    let cartEventTarget = /** @type {EventTarget} */ (this);

    const deferred = CartLinesUpdateEvent.createPromise();
    const cartMutationVersion = this.#markCartMutationStart();

    try {
      const dialogContent = document.getElementById('quick-add-modal-content');
      cartEventTarget = dialogContent?.querySelector('product-form-component') || this;

      // Dispatch synchronously so consumers can render optimistic skeleton rows.
      cartEventTarget.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'dialog',
          lines: eventLines,
          promise: deferred.promise,
          detail: {
            source: 'upsell-quick-add',
            sourceId: this.id || 'upsell-quick-add-trigger',
          },
        })
      );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      this.#debug('Upsell add response', {
        ok: response.ok,
        status: response.status,
        result,
      });

      if (!response.ok || result.status) {
        this.#showFormError(form, result.message || this.dataset.addError || 'Unable to add upsell item to cart.');

        cartEventTarget.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Unable to add upsell item to cart.',
            code: 'INVALID',
            detail: {
              source: 'upsell-quick-add',
              description: result.description,
              errors: result.errors,
            },
          })
        );

        this.#settleCartAdd(deferred, null, {
          source: 'upsell-quick-add',
          sourceId: this.id || 'upsell-quick-add-trigger',
          didError: true,
        });
        return;
      }

      const cart = await this.#fetchCartJson();
      this.#settleCartAdd(
        deferred,
        cart,
        {
          source: 'upsell-quick-add',
          sourceId: this.id || 'upsell-quick-add-trigger',
          didError: false,
          itemCount: typeof cart?.item_count === 'number' ? cart.item_count : undefined,
        },
        cartMutationVersion
      );

      if (companionItems.length > 0) {
        await this.#runCompanionCartSync(cartEventTarget);
      }
    } catch (error) {
      this.#showFormError(form, this.dataset.addError || 'Unable to add upsell item to cart.');
      deferred.reject(error);
      cartEventTarget.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during upsell add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      throw error;
    } finally {
      this.#isSubmittingUpsell = false;
    }
  };

  #captureMainProductState() {
    const mainProductForm = this.#resolveMainProductForm();

    const variantInput = mainProductForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      this.#mainVariantId = variantInput.value;
    }

    if (!this.#mainVariantId) {
      this.#mainVariantId = this.#resolveVariantIdFromUrl();
    }

    const mainVariantPicker = this.#resolveMainVariantPicker(mainProductForm);
    this.#debug('Main picker resolution', {
      hasMainProductForm: Boolean(mainProductForm),
      mainProductFormSelector: mainProductForm?.tagName,
      mainVariantPicker: mainVariantPicker ? mainVariantPicker.outerHTML?.slice?.(0, 240) : null,
    });

    const qtySelector = mainProductForm?.querySelector('quantity-selector-component');
    const qty = /** @type {{ getValue?: () => number | string }} */ (qtySelector)?.getValue?.();
    if (Number(qty) > 0) {
      this.#mainQty = Number(qty);
    } else {
      this.#mainQty = this.#resolveMainQuantity(mainProductForm);
    }

    this.#mainPrice = this.#resolveVariantPrice(mainProductForm);
    this.#mainProductId = this.#resolveMainProductId(mainProductForm);
    this.#mainProductHandle = this.#resolveMainProductHandle(mainProductForm);

    const variantPicker = this.#resolveMainVariantPicker(mainProductForm);
    this.#mainSizeValue = this.#extractMainSizeValue(variantPicker);
    this.#mainProductContext = this.#resolveMainProductContext(mainProductForm, variantPicker);
    this.#debug('Captured main product state', {
      mainVariantId: this.#mainVariantId,
      mainQty: this.#mainQty,
      mainSizeValue: this.#mainSizeValue,
      mainPrice: this.#mainPrice,
      mainProductId: this.#mainProductId,
      mainProductHandle: this.#mainProductHandle,
      mainProductContext: this.#mainProductContext,
    });
  }

  /** @returns {Element | Document} */
  #resolveCompanionCartScope() {
    const scopeSelector = this.dataset.companionCartScope?.trim();
    if (scopeSelector) {
      const scoped =
        this.querySelector(scopeSelector)
        || this.closest(scopeSelector)
        || document.querySelector(scopeSelector);
      if (scoped instanceof Element) return scoped;
    }

    const productInformation = this.#resolveMainProductForm()?.closest('[id^="ProductInformation"]');
    if (productInformation instanceof Element) return productInformation;

    const section = this.closest('.shopify-section');
    return section instanceof Element ? section : document;
  }

  /** @returns {Array<{ id: number, quantity: number }>} */
  #collectCompanionCartItems() {
    const scope = this.#resolveCompanionCartScope();
    /** @type {Array<{ id: number, quantity: number }>} */
    const items = [];

    for (const candidate of scope.querySelectorAll('[data-companion-cart-item]')) {
      if (
        candidate.matches('input[type="checkbox"], input[type="radio"]')
        && candidate instanceof HTMLInputElement
        && !candidate.checked
      ) {
        continue;
      }

      const variantHost =
        candidate.matches('[data-variant-id]')
          ? candidate
          : candidate.closest('[data-variant-id]');
      const variantId =
        candidate.getAttribute('data-variant-id')
        ?? variantHost?.getAttribute('data-variant-id')
        ?? (candidate instanceof HTMLInputElement && /^\d+$/.test(candidate.value) ? candidate.value : null);

      if (!variantId || !/^\d+$/.test(variantId)) continue;

      items.push({
        id: Number(variantId),
        quantity: this.#resolveCompanionCartQuantity(candidate, variantHost),
      });
    }

    return items;
  }

  /**
   * @param {Element | null} candidate
   * @param {Element | null} variantHost
   * @returns {number}
   */
  #resolveCompanionCartQuantity(candidate, variantHost) {
    const qtyInput =
      candidate?.matches('[data-companion-cart-quantity]')
        ? candidate
        : variantHost?.querySelector('[data-companion-cart-quantity], input[type="number"]');

    if (qtyInput instanceof HTMLInputElement && Number(qtyInput.value) > 0) {
      return Number(qtyInput.value);
    }

    for (const element of [candidate, variantHost].filter((node) => node instanceof Element)) {
      const dataQty = element.getAttribute('data-quantity');
      if (dataQty && Number(dataQty) > 0) return Number(dataQty);
    }

    return 1;
  }

  /**
   * @param {{ items: Array<{ id: number, quantity: number, properties?: Record<string, string> }> }} payload
   * @param {Array<{ id: number, quantity: number }>} companionItems
   */
  #mergeCompanionCartItems(payload, companionItems) {
    for (const companionItem of companionItems) {
      const existing = payload.items.find((item) => item.id === companionItem.id && !item.properties);
      if (existing) {
        existing.quantity += companionItem.quantity;
      } else {
        payload.items.push(companionItem);
      }
    }
  }

  /**
   * @param {string} path
   * @returns {((...args: unknown[]) => unknown) | null}
   */
  #resolveWindowCallable(path) {
    const keys = path.split('.').filter(Boolean);
    if (keys.length === 0) return null;

    const fn = keys.reduce((value, key) => {
      if (value && typeof value === 'object' && key in value) {
        return /** @type {Record<string, unknown>} */ (value)[key];
      }
      return undefined;
    }, /** @type {unknown} */ (window));

    return typeof fn === 'function' ? /** @type {(...args: unknown[]) => unknown} */ (fn) : null;
  }

  /**
   * @param {EventTarget} cartEventTarget
   */
  async #runCompanionCartSync(cartEventTarget) {
    document.dispatchEvent(
      new CustomEvent('theme:companion-cart-added', {
        bubbles: true,
        detail: {
          source: this.id || 'upsell-quick-add-trigger',
        },
      })
    );

    const syncPath = this.dataset.companionCartSync?.trim();
    if (!syncPath) return;

    const handler = this.#resolveWindowCallable(syncPath);
    if (!handler) return;

    const cart = await this.#fetchCartJson();
    if (!cart) return;

    try {
      await handler({ cart });
    } catch (error) {
      this.#debug('Companion cart sync handler failed', error);
      console.warn('[UpsellQuickAdd] Companion cart sync handler failed', error);
      return;
    }

    const refreshedCart = await this.#fetchCartJson();
    if (!refreshedCart) return;

    await this.#dispatchCartRefresh(cartEventTarget, refreshedCart);
  }

  /** @returns {Promise<Record<string, unknown> | null>} */
  async #fetchCartJson() {
    const cartPath = Theme?.routes?.cart_url || '/cart';
    const cartUrl = cartPath.endsWith('.js') ? cartPath : `${cartPath}.js`;

    try {
      const cartResponse = await fetch(cartUrl);
      if (!cartResponse.ok) return null;
      return await cartResponse.json();
    } catch {
      return null;
    }
  }

  /**
   * @param {EventTarget} cartEventTarget
   * @param {Record<string, unknown>} cart
   */
  async #dispatchCartRefresh(cartEventTarget, cart) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((section) => (section instanceof HTMLElement ? section.dataset.sectionId : null))
      .filter((sectionId) => Boolean(sectionId));

    if (sectionIds.length === 0) return;

    /** @type {Record<string, string>} */
    const sections = {};
    await Promise.all(
      sectionIds.map(async (sectionId) => {
        if (!sectionId) return;
        sections[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false);
      })
    );

    const cartItems = Array.isArray(cart.items) ? cart.items : [];
    /** @type {{ id: string, quantity: number }[]} */
    const lines =
      cartItems.length > 0
        ? cartItems.map((item) => {
            const line = /** @type {{ key?: string, id?: string | number, quantity?: number }} */ (item);
            return {
              id: String(line.key ?? line.id ?? ''),
              quantity: Number(line.quantity) || 0,
            };
          })
        : [{ id: 'companion-sync', quantity: 0 }];

    const deferred = CartLinesUpdateEvent.createPromise();

    cartEventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'dialog',
        lines,
        promise: deferred.promise,
        detail: {
          source: 'upsell-quick-add-companion-sync',
          sourceId: this.id || 'upsell-quick-add-trigger',
        },
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        resource: cart,
        items: cartItems,
        sections,
        source: 'upsell-quick-add-companion-sync',
        sourceId: this.id || 'upsell-quick-add-trigger',
        itemCount: typeof cart.item_count === 'number' ? cart.item_count : undefined,
      },
    });
  }

  /**
   * Settles the deferred CartLinesUpdateEvent promise for an upsell add.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred
   * @param {Record<string, unknown> | null} cart
   * @param {{ source: string, sourceId: string, didError: boolean, itemCount?: number }} detail
   * @param {number} [cartMutationVersion]
   */
  #settleCartAdd(deferred, cart, detail, cartMutationVersion) {
    const cartData = /** @type {{ item_count?: number, items?: unknown[] } | null} */ (cart);
    const lightweight =
      !detail.didError &&
      Boolean(cartData) &&
      typeof cartData?.item_count === 'number' &&
      (cartMutationVersion === undefined || this.#getCartMutationVersion() === cartMutationVersion);

    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: {
        ...detail,
        resource: cart ?? {},
        items: cartData?.items,
        lightweight,
      },
    });
  }

  /** @returns {number} */
  #markCartMutationStart() {
    const win = /** @type {Window & { __themeCartMutationVersion?: number }} */ (window);
    win.__themeCartMutationVersion = (win.__themeCartMutationVersion ?? 0) + 1;
    return win.__themeCartMutationVersion;
  }

  /** @returns {number} */
  #getCartMutationVersion() {
    return /** @type {Window & { __themeCartMutationVersion?: number }} */ (window).__themeCartMutationVersion ?? 0;
  }

  /**
   * @param {Element | null} mainProductForm
   * @returns {string | null}
   */
  #resolveMainProductId(mainProductForm) {
    const formProductId = mainProductForm?.getAttribute('data-product-id')?.trim();
    if (formProductId) return formProductId;

    const section = this.closest('.shopify-section');
    const sectionProductId = section?.getAttribute('data-product-id')?.trim();
    if (sectionProductId) return sectionProductId;

    const variantPicker = this.#resolveMainVariantPicker(mainProductForm);
    const pickerProductId = variantPicker?.getAttribute('data-product-id')?.trim();
    if (pickerProductId) return pickerProductId;

    return null;
  }

  /**
   * @param {Element | null} mainProductForm
   * @returns {string | null}
   */
  #resolveMainProductHandle(mainProductForm) {
    const productUrl =
      mainProductForm?.getAttribute('data-product-url')
      || this.closest('.shopify-section')?.querySelector('[data-product-url]')?.getAttribute('data-product-url')
      || '';
    if (productUrl) {
      const match = productUrl.match(/\/products\/([^/?#]+)/);
      if (match?.[1]) return match[1].toLowerCase();
    }
    return null;
  }

  /**
   * @param {Element | null} mainProductForm
   * @param {Element | null} variantPicker
   * @returns {{ title: string, url: string, imageSrc: string, imageAlt: string, selectedOptions: Array<{ name: string, value: string }> }}
   */
  #resolveMainProductContext(mainProductForm, variantPicker = null) {
    const section = this.closest('.shopify-section');
    const titleSource = section?.querySelector('.product-title a, .view-product-title a, .product-title, h1');
    const imageSource = section?.querySelector('.product-media-container img, .product-media img');
    const resolvedVariantPicker = variantPicker || this.#resolveMainVariantPicker(mainProductForm);

    const title = titleSource?.textContent?.trim() || '';
    const url = titleSource instanceof HTMLAnchorElement ? titleSource.href : window.location.href;
    const imageSrc = imageSource instanceof HTMLImageElement ? imageSource.currentSrc || imageSource.src : '';
    const imageAlt = imageSource instanceof HTMLImageElement ? imageSource.alt || title : title;

    return {
      title,
      url,
      imageSrc,
      imageAlt,
      selectedOptions: this.#resolveSelectedOptions(resolvedVariantPicker),
    };
  }

  /**
   * @param {Element | null} mainProductForm
   * @returns {Element | null}
   */
  #resolveMainVariantPicker(mainProductForm) {
    const inFormPicker = mainProductForm?.querySelector('variant-picker');
    if (inFormPicker) return inFormPicker;

    const section = this.closest('.shopify-section');
    const formDetails = mainProductForm?.closest('.product-details');

    if (formDetails) {
      const inDetails = [...formDetails.querySelectorAll('variant-picker')].find((picker) => !picker.closest('#quick-add-dialog'));
      if (inDetails) return inDetails;
    }

    if (section) {
      const inSection = [...section.querySelectorAll('variant-picker')].find((picker) => !picker.closest('#quick-add-dialog'));
      if (inSection) return inSection;
    }

    return [...document.querySelectorAll('variant-picker')].find((picker) => !picker.closest('#quick-add-dialog')) || null;
  }

  /** @returns {string | null} */
  #resolveMainPriceLabel() {
    return this.#formatPrice(this.#mainPrice);
  }

  /** @returns {string | null} */
  #resolveUpsellPriceLabel() {
    return this.#formatPrice(this.#upsellPrice);
  }

  /** @param {HTMLElement} modalContent */
  #hideUpsellInlinePrice(modalContent) {
    const priceElements = modalContent.querySelectorAll('product-price');
    for (const priceElement of priceElements) {
      if (!(priceElement instanceof HTMLElement)) continue;
      priceElement.hidden = true;
      priceElement.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * @param {HTMLElement | null | undefined} [modalContent]
   * @returns {string | null}
   */
  #resolveCombinedPriceLabel(modalContent) {
    const combinedMinorUnits = this.#mainPrice * this.#mainQty + this.#upsellPrice;

    if (combinedMinorUnits > 0) {
      return this.#formatPrice(combinedMinorUnits);
    }

    if (!this.#mainPrice) {
      this.#mainPrice = this.#resolveVariantPrice(this.#resolveMainProductForm());
    }

    if (!this.#upsellPrice && modalContent instanceof HTMLElement) {
      this.#upsellPrice = this.#resolveUpsellVariantPrice(modalContent);
    }

    const recomputedMinorUnits = this.#mainPrice * this.#mainQty + this.#upsellPrice;
    if (recomputedMinorUnits > 0) {
      return this.#formatPrice(recomputedMinorUnits);
    }

    return null;
  }

  /**
   * Format price to locale currency using Shopify format or Intl API
   * @param {number} minorUnits - Price in minor currency units (cents)
   * @param {string | null} [override] - Override value to use if formatting fails
   * @returns {string | null}
   */
  #formatPrice(minorUnits, override = null) {
    if (minorUnits <= 0) return override || null;

    const moneyFormat = /** @type {{ money_format?: string } | undefined} */ (window.Shopify)?.money_format;
    const currencyCode = this.#resolveCurrencyCode();

    if (moneyFormat) {
      return formatMoney(minorUnits, moneyFormat, currencyCode);
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(minorUnits / 100);
    } catch (_error) {
      return override || null;
    }
  }

  /**
   * @param {Array<{ name: string, value: string }>} selectedOptions
   * @returns {string}
   */
  #resolveSelectedVariantsText(selectedOptions) {
    if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) return '';

    return selectedOptions
      .filter((option) => option?.name && option?.value)
      .map((option) => `${option.value}`)
      .join(', ');
  }

  /**
   * @param {Element | null | undefined} variantPicker
   * @returns {Array<{ name: string, value: string }>}
   */
  #resolveSelectedOptions(variantPicker) {
    if (!variantPicker) return [];

    const selections = [];
    const fieldsets = [...variantPicker.querySelectorAll('fieldset.variant-option')];

    for (const fieldset of fieldsets) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      let optionName = fieldset.querySelector('legend')?.textContent?.trim() || '';
      if (!optionName) continue;

      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) {
        const optionValue = this.#resolveSizeOptionLabel(checkedInput.closest('label'), checkedInput);
        const normalizedName = optionValue && optionName.endsWith(optionValue)
          ? optionName.slice(0, optionName.length - optionValue.length).trim().replace(/[:\s]+$/u, '')
          : optionName;

        selections.push({
          name: normalizedName || optionName,
          value: optionValue,
        });
        continue;
      }

      const select = fieldset.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        const selectedText = select.selectedOptions?.[0]?.textContent?.trim() || select.value?.trim();
        if (selectedText) {
          const normalizedName = optionName.endsWith(selectedText)
            ? optionName.slice(0, optionName.length - selectedText.length).trim().replace(/[:\s]+$/u, '')
            : optionName;
          selections.push({ name: normalizedName || optionName, value: selectedText });
        }
      }
    }

    return selections;
  }

  /** @returns {string | null} */
  #resolveVariantIdFromUrl() {
    const variantParam = new URL(window.location.href).searchParams.get('variant');
    return variantParam?.trim() || null;
  }

  /**
   * @param {Element | null} mainProductForm
   * @returns {number}
   */
  #resolveMainQuantity(mainProductForm) {
    const quantityInput = mainProductForm?.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement && Number(quantityInput.value) > 0) {
      return Number(quantityInput.value);
    }

    return 1;
  }

  /** @returns {Element | null} */
  #resolveMainProductForm() {
    const section = this.closest('.shopify-section');
    if (!section) {
      return [...document.querySelectorAll('product-form-component')].find((component) => !component.closest('dialog')) || null;
    }

    const buyButtonsBlock = section.querySelector('.buy-buttons-block');
    const scopedForm = buyButtonsBlock?.querySelector('product-form-component');
    if (scopedForm) return scopedForm;

    return [...section.querySelectorAll('product-form-component')].find((component) => !component.closest('dialog')) || null;
  }

  /**
   * @param {Element | null} productForm
   * @returns {number}
   */
  #resolveVariantPrice(productForm) {
    if (!productForm) return 0;

    const variantInput = productForm.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
    if (!variantId) return 0;

    // Check cache first
    const isMainForm = productForm === this.#resolveMainProductForm();
    const cache = isMainForm ? this.#cachedMainVariants : this.#cachedUpsellVariants;
    
    if (cache) {
      const cached = cache.find((variant) => String(variant.id) === String(variantId));
      if (cached) return cached.price;
    }

    const inFormScript = productForm.querySelector('variant-picker script[type="application/json"]');
    const variantPicker = this.#resolveMainVariantPicker(productForm);
    const pickerScript = variantPicker?.querySelector('script[type="application/json"]');
    const variantScript = inFormScript || pickerScript;
    if (!(variantScript instanceof HTMLScriptElement)) return 0;

    try {
      const parsed = JSON.parse(variantScript.textContent || '[]');
      // Handle both array-of-variants and single selected-variant object formats
      const variants = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object'
          ? [parsed]
          : [];
      if (variants.length === 0) {
        this.#debug('Variant script produced no variants', { isMainForm, variantScriptContent: variantScript.textContent?.slice?.(0, 100) });
        return 0;
      }

      // Cache parsed variants
      if (isMainForm) {
        this.#cachedMainVariants = variants;
      } else {
        this.#cachedUpsellVariants = variants;
      }

      const selectedVariant = variants.find((variant) => String(variant?.id) === String(variantId));
      return typeof selectedVariant?.price === 'number' ? selectedVariant.price : 0;
    } catch (error) {
      this.#debug('Error parsing variant script', {
        isMainForm,
        errorMessage: error instanceof Error ? error.message : String(error),
        variantScriptContent: variantScript.textContent?.slice?.(0, 100),
      });
      return 0;
    }
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {number}
   */
  #resolveUpsellVariantPrice(modalContent) {
    const form = modalContent.querySelector('product-form-component form');
    if (!(form instanceof HTMLFormElement)) return 0;

    const variantId = this.#resolveUpsellVariantId(modalContent, form);
    if (!variantId) return 0;

    if (this.#cachedUpsellVariants) {
      const cached = this.#cachedUpsellVariants.find((v) => String(v.id) === String(variantId));
      if (cached) return cached.price;
    }

    // Search within modalContent directly — #resolveMainVariantPicker excludes dialog elements
    // in its fallback chain and would find the main product's variant picker instead.
    const variantScript = modalContent.querySelector('variant-picker script[type="application/json"]');
    if (!(variantScript instanceof HTMLScriptElement)) return 0;

    try {
      const parsed = JSON.parse(variantScript.textContent || '[]');
      // Handle both array-of-variants and single selected-variant object formats
      const variants = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object'
          ? [parsed]
          : [];
      if (variants.length === 0) return 0;

      this.#cachedUpsellVariants = variants;
      const selected = variants.find((v) => String(v?.id) === String(variantId));
      return typeof selected?.price === 'number' ? selected.price : 0;
    } catch {
      return 0;
    }
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {HTMLFormElement | null} [form]
   * @returns {string}
   */
  #resolveUpsellVariantId(modalContent, form = null) {
    const selectedOptionVariantId = this.#resolveUpsellVariantIdFromSelectedOption(modalContent);
    if (selectedOptionVariantId) {
      this.#selectedUpsellVariantId = selectedOptionVariantId;
      return selectedOptionVariantId;
    }

    const productFormComponent = form?.closest('product-form-component') || modalContent.querySelector('product-form-component');
    const scopedInput =
      productFormComponent?.querySelector('input[name="id"]')
      || form?.querySelector('input[name="id"]')
      || modalContent.querySelector('input[name="id"]');

    const inputValue = scopedInput instanceof HTMLInputElement ? scopedInput.value.trim() : '';
    if (inputValue) {
      this.#selectedUpsellVariantId = inputValue;
      return inputValue;
    }

    return this.#selectedUpsellVariantId || '';
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {string}
   */
  #resolveUpsellVariantIdFromSelectedOption(modalContent) {
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return '';

    const selectedOption = variantPicker.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLOptionElement || selectedOption instanceof HTMLInputElement)) {
      return '';
    }

    return selectedOption.dataset.variantId?.trim() || '';
  }

  /** @returns {string} */
  #resolveCurrencyCode() {
    return window.Shopify?.currency?.active || 'USD';
  }

  /**
   * @param {Element | null | undefined} variantPicker
   * @returns {string | null}
   */
  #extractMainSizeValue(variantPicker) {
    if (!variantPicker) return null;

    const fieldsets = [...variantPicker.querySelectorAll('fieldset.variant-option--buttons')];
    const sizeFieldset = this.#findSizeFieldset(fieldsets);
    if (!sizeFieldset) return null;

    const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return null;

    return this.#normalizeSizeValue(checked.value || '') || null;
  }

  /**
   * @param {Element | null | undefined} label
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  #resolveSizeOptionLabel(label, input) {
    if (label instanceof HTMLLabelElement) {
      const textElement = label.querySelector('[data-key="variant-option-text"]');
      const textValue = textElement?.textContent?.trim();
      if (textValue) return textValue;
    }

    return input.value?.trim() || '';
  }

  /**
   * @param {Element | null | undefined} label
   * @param {HTMLInputElement} input
   * @returns {Set<string>}
   */
  #resolveNormalizedOptionValues(label, input) {
    const values = new Set();

    const normalizedInputValue = this.#normalizeSizeValue(input.value || '');
    if (normalizedInputValue) {
      values.add(normalizedInputValue);
    }

    const optionLabel = this.#resolveSizeOptionLabel(label, input);
    const normalizedLabelValue = this.#normalizeSizeValue(optionLabel || '');
    if (normalizedLabelValue) {
      values.add(normalizedLabelValue);
    }

    return values;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeSizeValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/["']/g, '');
  }

  /**
   * @param {Element[]} fieldsets
   * @returns {Element | null}
   */
  #findSizeFieldset(fieldsets) {
    if (!fieldsets || fieldsets.length === 0) return null;

    const sizeByOptionId = fieldsets.find((fieldset) => {
      const optionId = fieldset.getAttribute('data-option-id') || '';
      return /(^|-)size$/i.test(optionId);
    });
    if (sizeByOptionId) return sizeByOptionId;

    return (
      fieldsets.find((fieldset) => {
        const legend = fieldset.querySelector('legend');
        return /size/i.test(legend?.textContent || '');
      }) || null
    );
  }

  /**
   * @param {unknown} entry
   * @returns {Set<string>}
   */
  #resolveMappedValuesFromEntry(entry) {
    if (!entry) return new Set();

    if (Array.isArray(entry)) {
      return new Set(entry.map((value) => this.#normalizeSizeValue(String(value))).filter(Boolean));
    }

    if (typeof entry === 'string') {
      return new Set([this.#normalizeSizeValue(entry)].filter(Boolean));
    }

    if (typeof entry === 'object') {
      const mappingObject = /** @type {{ patch?: unknown, upsell?: unknown, target?: unknown, upsell_sizes?: unknown }} */ (entry);

      if (typeof mappingObject.patch === 'string' && mappingObject.patch.trim()) {
        return new Set([this.#normalizeSizeValue(mappingObject.patch)].filter(Boolean));
      }

      const targetValues = mappingObject.upsell || mappingObject.target || mappingObject.upsell_sizes;
      if (Array.isArray(targetValues)) {
        return new Set(targetValues.map((value) => this.#normalizeSizeValue(String(value))).filter(Boolean));
      }
      if (typeof targetValues === 'string') {
        return new Set([this.#normalizeSizeValue(targetValues)].filter(Boolean));
      }
    }

    return new Set();
  }

  /**
   * @param {unknown} parsedMappings
   * @returns {Set<string>}
   */
  #resolveMasterProductMappings(parsedMappings) {
    if (!parsedMappings || typeof parsedMappings !== 'object' || !this.#mainSizeValue) {
      return new Set();
    }

    const root = /** @type {{ by_handle?: Record<string, unknown>, by_product?: Record<string, unknown>, products?: Record<string, unknown>, default_mappings?: unknown, defaults?: unknown }} */ (parsedMappings);
    const normalizedMainSize = this.#normalizeSizeValue(this.#mainSizeValue);
    const productId = this.#mainProductId ? String(this.#mainProductId) : '';
    const productHandle = this.#mainProductHandle || '';

    // Resolution tier 1: by_handle
    if (root.by_handle && typeof root.by_handle === 'object' && productHandle) {
      const handleMap = /** @type {Record<string, unknown>} */ (root.by_handle);
      const handleEntry = handleMap[productHandle] || null;
      if (handleEntry && typeof handleEntry === 'object') {
        const typedHandleEntry = /** @type {{ size_mappings?: Record<string, unknown>, mappings?: Record<string, unknown>, mapping?: Record<string, unknown>, sizes?: Record<string, unknown> }} */ (handleEntry);
        const scopedMappings =
          typedHandleEntry.size_mappings
          || typedHandleEntry.mappings
          || typedHandleEntry.mapping
          || typedHandleEntry.sizes
          || null;
        if (scopedMappings && typeof scopedMappings === 'object') {
          const scopedValue =
            scopedMappings[normalizedMainSize]
            || scopedMappings[this.#mainSizeValue]
            || scopedMappings[this.#mainSizeValue.toUpperCase()];
          const resolved = this.#resolveMappedValuesFromEntry(scopedValue);
          if (resolved.size > 0) {
            this.#debug('resolveMasterProductMappings: resolved via by_handle', { productHandle, normalizedMainSize });
            return resolved;
          }
        }
      }
    }

    // Resolution tier 2: by_product / products
    const productMap =
      (root.by_product && typeof root.by_product === 'object' ? root.by_product : null)
      || (root.products && typeof root.products === 'object' ? root.products : null)
      || null;

    if (productMap && productId) {
      const productEntry =
        productMap[productId]
        || productMap[Number(productId)]
        || null;

      if (productEntry && typeof productEntry === 'object') {
        const typedProductEntry = /** @type {{ size_mappings?: Record<string, unknown>, mappings?: Record<string, unknown>, mapping?: Record<string, unknown>, sizes?: Record<string, unknown> }} */ (productEntry);
        const scopedMappings =
          typedProductEntry.size_mappings
          || typedProductEntry.mappings
          || typedProductEntry.mapping
          || typedProductEntry.sizes
          || null;

        if (scopedMappings && typeof scopedMappings === 'object') {
          const scopedValue =
            scopedMappings[normalizedMainSize]
            || scopedMappings[this.#mainSizeValue]
            || scopedMappings[this.#mainSizeValue.toUpperCase()];
          const resolved = this.#resolveMappedValuesFromEntry(scopedValue);
          if (resolved.size > 0) return resolved;
        }
      }
    }

    if (root.default_mappings && typeof root.default_mappings === 'object') {
      const defaultMappings = /** @type {Record<string, unknown>} */ (root.default_mappings);
      const fallbackValue = defaultMappings[normalizedMainSize] || defaultMappings[this.#mainSizeValue];
      const fallbackResolved = this.#resolveMappedValuesFromEntry(fallbackValue);
      if (fallbackResolved.size > 0) return fallbackResolved;
    }

    if (root.defaults && typeof root.defaults === 'object') {
      const defaults = /** @type {Record<string, unknown>} */ (root.defaults);
      const fallbackValue = defaults[normalizedMainSize] || defaults[this.#mainSizeValue];
      const fallbackResolved = this.#resolveMappedValuesFromEntry(fallbackValue);
      if (fallbackResolved.size > 0) return fallbackResolved;
    }

    return new Set();
  }

  /** @returns {Set<string>} */
  #resolveMappedSizeValues() {
    const rawMappings = this.dataset.sizeMappings;
    if (!rawMappings) return new Set();

    try {
      const parsedMappings = JSON.parse(rawMappings);

      const masterMappings = this.#resolveMasterProductMappings(parsedMappings);
      if (masterMappings.size > 0) {
        this.#debug('resolveMappedSizeValues: using product-aware master mappings', {
          mainProductId: this.#mainProductId,
          mainSizeValue: this.#mainSizeValue,
          resolved: [...masterMappings],
        });
        return masterMappings;
      }

      const hasMasterShape =
        parsedMappings
        && typeof parsedMappings === 'object'
        && !Array.isArray(parsedMappings)
        && ('by_handle' in parsedMappings || 'by_product' in parsedMappings || 'products' in parsedMappings);
      if (hasMasterShape) {
        this.#debug('resolveMappedSizeValues: master mapping did not resolve, using legacy/fallback parsing', {
          mainProductId: this.#mainProductId,
          mainProductHandle: this.#mainProductHandle,
          mainSizeValue: this.#mainSizeValue,
          hasByHandle: Boolean(parsedMappings.by_handle),
          hasByProduct: Boolean(parsedMappings.by_product),
          hasProducts: Boolean(parsedMappings.products),
        });
      }

      if (Array.isArray(parsedMappings)) {
        const isSimpleArray = parsedMappings.every((item) => typeof item === 'string');
        if (isSimpleArray) {
          return new Set(parsedMappings.map((item) => this.#normalizeSizeValue(item)).filter(Boolean));
        }

        if (this.#mainSizeValue) {
          for (const item of parsedMappings) {
            if (!item || typeof item !== 'object') continue;

            const source = String(item.main || item.main_size || item.source || '').trim().toLowerCase();
            const normalizedSource = this.#normalizeSizeValue(source);
            if (!normalizedSource || normalizedSource !== this.#mainSizeValue) continue;

            const targetValues = item.upsell || item.target || item.upsell_sizes || [];
            if (Array.isArray(targetValues)) {
              return new Set(targetValues.map((value) => this.#normalizeSizeValue(String(value))).filter(Boolean));
            }
            if (typeof targetValues === 'string') {
              return new Set([this.#normalizeSizeValue(targetValues)]);
            }
          }
        }
      }

      if (parsedMappings && typeof parsedMappings === 'object' && this.#mainSizeValue) {
        const normalizedKey = this.#normalizeSizeValue(this.#mainSizeValue);
        const mapped = parsedMappings[normalizedKey] || parsedMappings[this.#mainSizeValue] || parsedMappings[this.#mainSizeValue.toUpperCase()];

        const resolved = this.#resolveMappedValuesFromEntry(mapped);
        if (resolved.size > 0) {
          return resolved;
        }
      }
    } catch (error) {
      this.#debug('Error parsing size mappings JSON', {
        errorMessage: error instanceof Error ? error.message : String(error),
        rawMappings: rawMappings?.slice?.(0, 100),
      });
      return this.#parseSizeMappingsFallback(rawMappings);
    }

    this.#debug('resolveMappedSizeValues: no JSON mapping matched, using text fallback parser', {
      mainProductId: this.#mainProductId,
      mainSizeValue: this.#mainSizeValue,
    });
    return this.#parseSizeMappingsFallback(rawMappings);
  }

  /**
   * @param {string} rawMappings
   * @returns {Set<string>}
   */
  #parseSizeMappingsFallback(rawMappings) {
    const content = String(rawMappings || '').trim();
    if (!content) return new Set();

    const normalizedMainSize = this.#normalizeSizeValue(this.#mainSizeValue || '');
    const lines = content
      .split(/\r?\n|;/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (normalizedMainSize) {
      for (const line of lines) {
        const match = line.match(/^(.+?)\s*(?:=>|->|:|=)\s*(.+)$/u);
        if (!match) continue;

        const sourceSize = this.#normalizeSizeValue(match[1] || '');
        if (sourceSize !== normalizedMainSize) continue;

        return this.#splitMappedValues(match[2] || '');
      }
    }

    return this.#splitMappedValues(content);
  }

  /** @returns {boolean} */
  #isCustomMessageRequired() {
    const requiredValue = this.dataset.customMessageRequired;
    if (requiredValue === undefined) return false;

    const normalized = String(requiredValue).trim().toLowerCase();
    return normalized !== '' && normalized !== 'false' && normalized !== '0' && normalized !== 'no';
  }

  /**
   * @param {string} value
   * @returns {Set<string>}
   */
  #splitMappedValues(value) {
    return new Set(
      String(value || '')
        .split(/[,|\r\n]/)
        .map((entry) => this.#normalizeSizeValue(entry))
        .filter(Boolean)
    );
  }

  /**
   * @param {string} message
   * @param {unknown} [context]
   */
  #debug(message, context) {
    if (!this.#isDebugEnabled()) return;

    const prefix = '[UpsellQuickAdd]';
    const entry = {
      message,
      context,
      timestamp: new Date().toISOString(),
    };
    this.#debugLog.push(entry);
    if (this.#debugLog.length > 30) {
      this.#debugLog.shift();
    }

    this.#renderDebugPanel();

    if (context === undefined) {
      console.log(prefix, message);
      return;
    }

    console.log(prefix, message, context);
  }

  #renderDebugPanel() {
    if (!this.#isDebugEnabled()) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    let panel = modalContent.querySelector('[data-upsell-debug-panel]');
    if (!(panel instanceof HTMLElement)) {
      panel = document.createElement('pre');
      panel.setAttribute('data-upsell-debug-panel', '');
      panel.className = 'upsell-debug-panel quick-add-visible';
      modalContent.appendChild(panel);
    }

    panel.textContent = JSON.stringify(
      {
        main: {
          variantId: this.#mainVariantId,
          qty: this.#mainQty,
          size: this.#mainSizeValue,
          price: this.#mainPrice,
          selectedOptions: this.#mainProductContext.selectedOptions,
        },
        upsell: {
          price: this.#upsellPrice,
        },
        recentLogs: this.#debugLog,
      },
      null,
      2
    );
  }

  /** @returns {boolean} */
  #isDebugEnabled() {
    if (typeof this.#debugEnabled === 'boolean') return this.#debugEnabled;

    const fromData = this.dataset.debug === 'true' || this.dataset.debug === '1';
    const fromQuery = new URL(window.location.href).searchParams.get('upsellDebug') === '1';
    const fromStorage = window.localStorage?.getItem('upsellQuickAddDebug') === '1';

    this.#debugEnabled = fromData || fromQuery || fromStorage;
    return this.#debugEnabled;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showFormError(form, message) {
    const errorTarget = form.querySelector('[data-upsell-form-error]');
    if (!(errorTarget instanceof HTMLElement)) return;

    errorTarget.textContent = message;
    errorTarget.setAttribute('aria-hidden', 'false');
  }

  /**
   * @param {HTMLButtonElement | null} button
   * @param {boolean} isLoading
   */
  #setButtonLoadingState(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    button.disabled = button.dataset.wasDisabled === 'true';
    button.removeAttribute('aria-busy');
    delete button.dataset.wasDisabled;
  }

  /** @param {HTMLElement} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }
}

if (!customElements.get('upsell-line-properties')) {
  customElements.define('upsell-line-properties', UpsellLineProperties);
}

if (!customElements.get('upsell-quick-add-trigger')) {
  customElements.define('upsell-quick-add-trigger', UpsellQuickAddTrigger);
}
